import { useCallback, useMemo } from 'react';
import {
	PublicKey,
	TransactionInstruction,
	VersionedTransaction,
} from '@solana/web3.js';
import { notify } from 'src/utils/notifications';
import useCurrentAuthority from '../../useCurrentAuthority';
import useVelocityAccountStore, {
	AccountData,
} from 'src/stores/useVelocityAccountsStore';
import { MAIN_POOL_ID } from '@velocity-exchange/common';
import useVelocityClient from '../../useVelocityClient';
import useVelocityClientIsReady from '../../useVelocityClientIsReady';
import { useGetTxParams } from '../../velocityActions/transactions/useGetTxParams';
import { DEFAULT_PUBLIC_KEY } from 'src/@types/types';
import useCurrentUserAccount from 'src/hooks/useCurrentUserAccount';
import chunk from 'lodash/chunk';
import { UiEnhancedIWalletV2 } from 'src/utils/wallets/IWalletV2';
import { useSponsoredSignVelocityActionWithFallback } from 'src/hooks/wallets/useSponsoredSignVelocityActionWithFallback';
import useIsSolflareWalletConnected from 'src/hooks/useIsSolflareWalletConnected';

const MAX_ACCOUNTS_PER_TX = 8;

const delegateableFilter = (account: AccountData) => {
	return (
		account.delegate.equals(DEFAULT_PUBLIC_KEY) &&
		account.poolId === MAIN_POOL_ID
	);
};

const DEFAULT_SUBACCOUNTS: AccountData[] = [];

export const useSubAccountsDelegation = () => {
	const authority = useCurrentAuthority();
	const velocityClient = useVelocityClient();
	const velocityClientIsReady = useVelocityClientIsReady();
	const accounts = useVelocityAccountStore((s) => s.accounts);
	const currentUserAccount = useCurrentUserAccount();

	const getAccountsStore = useVelocityAccountStore((s) => s.get);
	const getTxParams = useGetTxParams();
	const isSolflareWalletConnected = useIsSolflareWalletConnected();

	const allAuthorityAccounts = useMemo(() => {
		if (!authority) return DEFAULT_SUBACCOUNTS;

		return Object.values(accounts)
			.filter((acc): acc is AccountData => acc !== undefined)
			.filter((acc) => acc.authority.equals(authority)); // filters out accounts delegated to connected authority
	}, [authority, accounts]);

	const allUndelegatedAuthorityAccounts = useMemo(() => {
		return allAuthorityAccounts.filter(delegateableFilter);
	}, [allAuthorityAccounts]);

	const isCurrentUserAccountDelegatable =
		!!currentUserAccount && delegateableFilter(currentUserAccount);

	const getAuthorityAccounts = useCallback(() => {
		if (!authority) return [];

		const userAccounts = getAccountsStore().accounts;
		return Object.values(userAccounts)
			.filter((acc) => acc?.authority.equals(authority))
			.filter((acc) => acc !== undefined);
	}, [getAccountsStore, authority]);

	const setupDelegateAction = useCallback(
		async (
			wallet: UiEnhancedIWalletV2,
			newDelegate: PublicKey,
			overrideExistingDelegates: boolean
		) => {
			if (!velocityClientIsReady || !velocityClient || !authority) return;

			const authorityAccounts = getAuthorityAccounts();

			const delegatableAccounts = overrideExistingDelegates
				? authorityAccounts
				: authorityAccounts.filter(delegateableFilter);

			if (!delegatableAccounts.length) {
				notify({
					type: 'error',
					message: 'No subaccounts to set up delegates for.',
				});
				return;
			}

			const txParams = getTxParams({ useSimulatedComputeUnits: true });

			const buildDelegateIxs = (delegateAccounts: AccountData[]) =>
				Promise.all(
					delegateAccounts.map((account) =>
						velocityClient.getUpdateUserDelegateIx(newDelegate, {
							subAccountId: account.userId,
							userAccountPublicKey: account.pubKey,
							authority: velocityClient.wallet.publicKey,
						})
					)
				);

			// Single transaction if 8 or fewer accounts
			if (delegatableAccounts.length <= MAX_ACCOUNTS_PER_TX) {
				const ixs = await buildDelegateIxs(delegatableAccounts);
				return await wallet.signAndSendTransaction(ixs, txParams);
			}

			// Multiple transactions for more than 8 accounts
			const accountChunks = chunk(delegatableAccounts, MAX_ACCOUNTS_PER_TX);

			const instructionsMap: Record<string, TransactionInstruction[]> = {};
			for (const [i, accountChunk] of accountChunks.entries()) {
				instructionsMap[`chunk_${i}`] = await buildDelegateIxs(accountChunk);
			}

			const { signedTxMap } = await wallet.buildAndSignTransactionsMap(
				instructionsMap,
				txParams
			);

			const txSigs = await Promise.all(
				Object.values(signedTxMap)
					.filter(Boolean)
					.map((signedTx) =>
						velocityClient.sendTransaction(
							signedTx as VersionedTransaction,
							undefined,
							undefined,
							true
						)
					)
			);

			return txSigs[0]?.txSig;
		},
		[
			velocityClientIsReady,
			velocityClient,
			authority,
			getAuthorityAccounts,
			getTxParams,
		]
	);

	// there is an issue with Solflare not correctly signing the transaction when attempting to delegate with fee payer sponsorship
	const wrappedSetupDelegate = useSponsoredSignVelocityActionWithFallback(
		setupDelegateAction,
		isSolflareWalletConnected
	);

	/**
	 * Sets up the given authority as delegate for all subaccounts of current authority.
	 * If there are more than 8 subaccounts, chunks into multiple transactions and uses signAll.
	 */
	const setupDelegateForSubaccounts = useCallback(
		async (newDelegate: PublicKey, overrideExistingDelegates = false) => {
			try {
				return await wrappedSetupDelegate(
					newDelegate,
					overrideExistingDelegates
				);
			} catch (e) {
				console.error('setupDelegateForAllSubaccounts ~ err:', e);
				throw e;
			}
		},
		[wrappedSetupDelegate]
	);

	const devRemoveAllSubAccountsDelegates = useCallback(async () => {
		if (!velocityClientIsReady || !velocityClient || !authority) return;

		const authorityAccounts = getAuthorityAccounts();
		const delegatableAccounts = authorityAccounts.filter(
			(acc) => !acc.delegate.equals(DEFAULT_PUBLIC_KEY)
		);

		if (delegatableAccounts.length === 0) {
			notify({
				type: 'error',
				message: 'No subaccounts to remove delegates from.',
			});
			return;
		}

		const ixs = await Promise.all(
			delegatableAccounts.map((account) =>
				velocityClient.getUpdateUserDelegateIx(DEFAULT_PUBLIC_KEY, {
					subAccountId: account.userId,
					userAccountPublicKey: account.pubKey,
					authority: velocityClient.wallet.publicKey,
				})
			)
		);

		const tx = await velocityClient.buildTransaction(
			ixs,
			getTxParams({ useSimulatedComputeUnits: true })
		);
		await velocityClient.sendTransaction(tx);

		notify({
			type: 'success',
			message: 'Removed all subaccount delegates.',
		});
	}, [
		velocityClient,
		velocityClientIsReady,
		authority,
		getAuthorityAccounts,
		getTxParams,
	]);

	return {
		setupDelegateForSubaccounts,
		devRemoveAllSubAccountsDelegates,
		isCurrentUserAccountDelegatable,
		allAuthorityAccounts,
		allUndelegatedAuthorityAccounts,
	};
};
